import React, { useState, useEffect, useRef } from 'react';
import { 
  Battery, 
  Shield, 
  Zap, 
  Clock, 
  Award,
  Target,
  Eye,
  TrendingUp,
  Users,
  Globe,
  Cpu,
  Database,
  Code,
  Brain,
  ArrowRight,
  CheckCircle,
  Star,
  Layers,
  Activity
} from 'lucide-react';
import './AboutSection.css';

const AboutSection = () => {
  const [visibleSection, setVisibleSection] = useState('');
  const [countUpValues, setCountUpValues] = useState({
    experience: 0,
    teamMembers: 0,
    projects: 0,
    satisfaction: 0
  });
  const [activeTimeline, setActiveTimeline] = useState(0);
  const sectionRef = useRef(null);

  // Enhanced company features with more details
  const companyFeatures = [
    { 
      icon: <Battery size={28} />, 
      title: 'Advanced Power Management', 
      description: 'Uninterrupted operation with smart backup systems',
      details: ['48-hour battery backup', 'Auto power optimization', 'Smart charging cycles'],
      color: 'blue'
    },
    { 
      icon: <Shield size={28} />, 
      title: 'Enterprise Security', 
      description: 'Military-grade encryption and data protection',
      details: ['256-bit encryption', 'Multi-factor auth', 'GDPR compliant'],
      color: 'green'
    },
    { 
      icon: <Zap size={28} />, 
      title: 'Lightning Performance', 
      description: 'Optimized for speed and efficiency',
      details: ['Sub-second response', '99.9% uptime', 'Auto-scaling'],
      color: 'yellow'
    },
    { 
      icon: <Clock size={28} />, 
      title: 'Real-time Synchronization', 
      description: 'Instant data updates across all platforms',
      details: ['Live sync', 'Conflict resolution', 'Offline capability'],
      color: 'purple'
    }
  ];

  // Enhanced company stats with animations
  const companyStats = [
    { 
      number: 5, 
      suffix: '+', 
      label: 'Years Experience',
      icon: <Award size={24} />,
      color: 'blue'
    },
    { 
      number: 13, 
      suffix: '+', 
      label: 'Team Members',
      icon: <Users size={24} />,
      color: 'purple'
    },
    { 
      number: 103, 
      suffix: '+', 
      label: 'Projects Completed',
      icon: <TrendingUp size={24} />,
      color: 'green'
    },
    { 
      number: 98, 
      suffix: '%', 
      label: 'Client Satisfaction',
      icon: <Star size={24} />,
      color: 'yellow'
    }
  ];

  // Company timeline/milestones
  const timeline = [
    {
      year: '2019',
      title: 'Company Founded',
      description: 'Started with a vision to revolutionize educational technology',
      icon: <Target size={20} />
    },
    {
      year: '2020',
      title: 'First ERP Launch',
      description: 'Deployed our first comprehensive ERP system for schools',
      icon: <Database size={20} />
    },
    {
      year: '2021',
      title: 'AI Integration',
      description: 'Introduced facial recognition and AI-powered analytics',
      icon: <Brain size={20} />
    },
    {
      year: '2022',
      title: 'RFID Innovation',
      description: 'Launched advanced RFID systems with 12-meter range',
      icon: <Activity size={20} />
    },
    {
      year: '2023',
      title: 'Regional Expansion',
      description: 'Expanded services across Northeast India',
      icon: <Globe size={20} />
    },
    {
      year: '2024',
      title: 'Next-Gen Platform',
      description: 'Building the future of educational technology',
      icon: <Layers size={20} />
    }
  ];

  // Core values with interactive elements
  const coreValues = [
    {
      icon: <Eye size={32} />,
      title: 'Innovation',
      description: 'Constantly pushing the boundaries of what\'s possible in educational technology',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Shield size={32} />,
      title: 'Reliability',
      description: 'Building robust systems that educational institutions can depend on 24/7',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: <Users size={32} />,
      title: 'Partnership',
      description: 'Working closely with clients to understand and exceed their expectations',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Excellence',
      description: 'Delivering exceptional quality in every solution we create and deploy',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSection(entry.target.dataset.section);
          }
        });
      },
      { threshold: 0.3 }
    );

    const sections = sectionRef.current?.querySelectorAll('[data-section]');
    sections?.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Count up animation
  useEffect(() => {
    if (visibleSection === 'stats') {
      companyStats.forEach((stat, index) => {
        let currentCount = 0;
        const increment = stat.number / 100;
        const timer = setInterval(() => {
          currentCount += increment;
          if (currentCount >= stat.number) {
            currentCount = stat.number;
            clearInterval(timer);
          }
          setCountUpValues(prev => ({
            ...prev,
            [index === 0 ? 'experience' : index === 1 ? 'teamMembers' : index === 2 ? 'projects' : 'satisfaction']: Math.floor(currentCount)
          }));
        }, 30);
      });
    }
  }, [visibleSection]);

  // Timeline auto-progression
  useEffect(() => {
    const timelineInterval = setInterval(() => {
      setActiveTimeline((prev) => (prev + 1) % timeline.length);
    }, 3000);

    return () => clearInterval(timelineInterval);
  }, []);

  return (
    <section id="about" className="about" ref={sectionRef}>
      <div className="about__container">
        {/* Header with enhanced animations */}
        <div className="about__header" data-section="header">
          <div className="about__badge">
            <Globe className="about__badge-icon" />
            <span>About Our Journey</span>
          </div>
          <h2 className="about__title">
            <span className="about__title-line">Transforming Education</span>
            <span className="about__title-gradient">Through Technology</span>
          </h2>
          <p className="about__subtitle">
            Leading technology company specializing in innovative software solutions 
            and SaaS products for educational institutions and businesses across 
            the Northeast region with cutting-edge AI and automation.
          </p>
        </div>

        {/* Mission & Vision with interactive cards */}
        <div className="about__mission-vision" data-section="mission">
          <div className="about__mission-card">
            <div className="about__card-header">
              <div className="about__card-icon about__card-icon--mission">
                <Target size={32} />
              </div>
              <h3 className="about__card-title">Our Mission</h3>
            </div>
            <div className="about__card-content">
              <p className="about__card-description">
                To revolutionize how educational institutions and businesses manage 
                their operations through cutting-edge technology solutions. We believe 
                in creating software that's not just functional, but transformative.
              </p>
              <div className="about__card-features">
                <div className="about__feature-item">
                  <CheckCircle size={16} />
                  <span>Student-centered design</span>
                </div>
                <div className="about__feature-item">
                  <CheckCircle size={16} />
                  <span>Scalable architecture</span>
                </div>
                <div className="about__feature-item">
                  <CheckCircle size={16} />
                  <span>Future-ready solutions</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="about__vision-card">
            <div className="about__card-header">
              <div className="about__card-icon about__card-icon--vision">
                <Eye size={32} />
              </div>
              <h3 className="about__card-title">Our Vision</h3>
            </div>
            <div className="about__card-content">
              <p className="about__card-description">
                To become the leading technology partner for educational institutions 
                across Northeast India, providing innovative solutions that enhance 
                efficiency, security, and growth in the digital age.
              </p>
              <div className="about__card-features">
                <div className="about__feature-item">
                  <CheckCircle size={16} />
                  <span>Regional leadership</span>
                </div>
                <div className="about__feature-item">
                  <CheckCircle size={16} />
                  <span>Innovation excellence</span>
                </div>
                <div className="about__feature-item">
                  <CheckCircle size={16} />
                  <span>Sustainable growth</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Stats with animations */}
        <div className="about__stats" data-section="stats">
          <div className="about__stats-header">
            <h3 className="about__stats-title">Our Impact in Numbers</h3>
            <p className="about__stats-subtitle">Proven track record of success and growth</p>
          </div>
          <div className="about__stats-grid">
            {companyStats.map((stat, index) => (
              <div key={index} className={`about__stat about__stat--${stat.color}`}>
                <div className="about__stat-icon">
                  {stat.icon}
                </div>
                <div className="about__stat-content">
                  <div className="about__stat-number">
                    {index === 0 ? countUpValues.experience : 
                     index === 1 ? countUpValues.teamMembers :
                     index === 2 ? countUpValues.projects :
                     countUpValues.satisfaction}
                    <span className="about__stat-suffix">{stat.suffix}</span>
                  </div>
                  <div className="about__stat-label">{stat.label}</div>
                </div>
                <div className="about__stat-progress">
                  <div 
                    className="about__stat-progress-bar"
                    style={{ '--progress': `${stat.number}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Company Timeline */}
        <div className="about__timeline" data-section="timeline">
          <div className="about__timeline-header">
            <h3 className="about__timeline-title">Our Journey</h3>
            <p className="about__timeline-subtitle">Milestones that shaped our success</p>
          </div>
          <div className="about__timeline-container">
            {timeline.map((item, index) => (
              <div 
                key={index} 
                className={`about__timeline-item ${index === activeTimeline ? 'about__timeline-item--active' : ''}`}
                onClick={() => setActiveTimeline(index)}
              >
                <div className="about__timeline-marker">
                  <div className="about__timeline-icon">
                    {item.icon}
                  </div>
                </div>
                <div className="about__timeline-content">
                  <div className="about__timeline-year">{item.year}</div>
                  <h4 className="about__timeline-event-title">{item.title}</h4>
                  <p className="about__timeline-description">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Core Values */}
        {/* <div className="about__values" data-section="values">
          <div className="about__values-header">
            <h3 className="about__values-title">Our Core Values</h3>
            <p className="about__values-subtitle">The principles that guide everything we do</p>
          </div>
          <div className="about__values-grid">
            {coreValues.map((value, index) => (
              <div key={index} className="about__value-card">
                <div className={`about__value-icon bg-gradient-to-br ${value.gradient}`}>
                  {value.icon}
                </div>
                <h4 className="about__value-title">{value.title}</h4>
                <p className="about__value-description">{value.description}</p>
                <div className="about__value-hover-effect"></div>
              </div>
            ))}
          </div>
        </div> */}

        {/* Enhanced Features Grid */}
        <div className="about__features" data-section="features">
          <div className="about__features-header">
            <h3 className="about__features-title">Why Choose Our Technology</h3>
            <p className="about__features-subtitle">Advanced features that set us apart</p>
          </div>
          <div className="about__features-grid">
            {companyFeatures.map((feature, index) => (
              <div key={index} className={`about__feature-card about__feature-card--${feature.color}`}>
                <div className="about__feature-header">
                  <div className="about__feature-icon">
                    {feature.icon}
                  </div>
                  <h4 className="about__feature-title">{feature.title}</h4>
                </div>
                <p className="about__feature-description">{feature.description}</p>
                <div className="about__feature-details">
                  {feature.details.map((detail, idx) => (
                    <div key={idx} className="about__feature-detail">
                      <div className="about__feature-detail-dot"></div>
                      <span>{detail}</span>
                    </div>
                  ))}
                </div>
                <div className="about__feature-overlay"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="about__cta" data-section="cta">
          <div className="about__cta-content">
            <h3 className="about__cta-title">Ready to Transform Your Institution?</h3>
            <p className="about__cta-description">
              Join hundreds of educational institutions that trust us with their technology needs.
              Let's build the future of education together.
            </p>
            <div className="about__cta-buttons">
              <button className="about__cta-primary">
                <span>Schedule a Demo</span>
                <ArrowRight size={20} />
              </button>
              <button className="about__cta-secondary">
                <span>Learn More</span>
                <Code size={20} />
              </button>
            </div>
          </div>
          <div className="about__cta-visual">
            <div className="about__cta-stats">
              <div className="about__cta-stat">
                <div className="about__cta-stat-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="about__cta-stat-info">
                  <span className="about__cta-stat-number">23+</span>
                  <span className="about__cta-stat-label">Happy Clients</span>
                </div>
              </div>
              <div className="about__cta-stat">
                <div className="about__cta-stat-icon">
                  <Award size={24} />
                </div>
                <div className="about__cta-stat-info">
                  <span className="about__cta-stat-number">99.9%</span>
                  <span className="about__cta-stat-label">Uptime SLA</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;